'use client';

import { useRef, useEffect } from 'react';

const MAX_ROTATION = 8;
const LERP_FACTOR = 0.1; // 낮을수록 부드러움 (0.05~0.15 권장)

const ProductMockup = () => {
  const cardRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  const isHoveringRef = useRef(false);

  // Check for fine pointer (mouse) - skip tilt on touch devices
  const isFinePointer = () => typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;

  // Linear interpolation
  const lerp = (start: number, end: number, factor: number) => start + (end - start) * factor;

  const animate = () => {
    const card = cardRef.current;
    if (!card) return;

    currentRef.current.x = lerp(currentRef.current.x, targetRef.current.x, LERP_FACTOR);
    currentRef.current.y = lerp(currentRef.current.y, targetRef.current.y, LERP_FACTOR);

    const rotateY = currentRef.current.x * MAX_ROTATION;
    const rotateX = currentRef.current.y * -MAX_ROTATION;

    card.style.transform = `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;

    // 목표에 거의 도달하면 애니메이션 중지
    const dx = Math.abs(targetRef.current.x - currentRef.current.x);
    const dy = Math.abs(targetRef.current.y - currentRef.current.y);

    if (dx > 0.001 || dy > 0.001 || isHoveringRef.current) {
      rafRef.current = requestAnimationFrame(animate);
    } else {
      rafRef.current = null;
    }
  };

  const startAnimation = () => {
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(animate);
    }
  };

  const handleMouseEnter = () => {
    if (!isFinePointer()) return;
    const card = cardRef.current;
    if (!card) return;
    rectRef.current = card.getBoundingClientRect();
    isHoveringRef.current = true;
    startAnimation();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isFinePointer()) return;
    const rect = rectRef.current;
    if (!rect) return;

    targetRef.current.x = (e.clientX - rect.left) / rect.width - 0.5;
    targetRef.current.y = (e.clientY - rect.top) / rect.height - 0.5;
  };

  const handleMouseLeave = () => {
    if (!isFinePointer()) return;
    isHoveringRef.current = false;
    targetRef.current = { x: 0, y: 0 };
    rectRef.current = null;
    // 애니메이션이 자연스럽게 0으로 돌아감
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <section id='about' className='px-4 md:px-6 pb-20 max-w-[1100px] 2xl:max-w-[1400px] mx-auto scroll-mt-32'>
      <div
        ref={cardRef}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className='rounded-xl overflow-hidden'
        style={{
          boxShadow:
            '0 50px 120px -20px rgba(0,0,0,0.3), 0 25px 60px -15px rgba(124,58,237,0.25), 0 0 0 1px rgba(0,0,0,0.1), 0 0 80px -20px rgba(124,58,237,0.15)',
          transform: 'perspective(1200px) rotateX(0deg) rotateY(0deg)',
          willChange: 'transform',
        }}
      >
        {/* Chrome-style title bar */}
        <div className='flex items-center gap-2 px-4 py-2.5 border-b border-border bg-card'>
          <div className='flex items-center gap-1.5 mr-3'>
            <div className='w-3 h-3 rounded-full' style={{ background: '#ff5f57' }} />
            <div className='w-3 h-3 rounded-full' style={{ background: '#ffbd2e' }} />
            <div className='w-3 h-3 rounded-full' style={{ background: '#28c840' }} />
          </div>
          <div className='flex-1 flex items-center justify-center'>
            <div
              className='flex items-center gap-2 px-4 py-1 rounded-md text-xs max-w-md w-full'
              style={{
                background: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                color: 'hsl(var(--muted-foreground))',
              }}
            >
              <svg
                width='12'
                height='12'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <rect width='18' height='11' x='3' y='11' rx='2' ry='2' />
                <path d='M7 11V7a5 5 0 0 1 10 0v4' />
              </svg>
              <span>tindlo.com - Let teams rule the tasks.</span>
            </div>
          </div>
          <div className='w-[54px]' />
        </div>

        <div className='relative w-full aspect-video bg-black'>
          <iframe
            className='absolute inset-0 w-full h-full'
            src='https://www.youtube-nocookie.com/embed/ATKeROs4azM?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0&playsinline=1'
            title='Tindlo Demo'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
};

export default ProductMockup;
